import React, { useState, useEffect, useMemo, useContext } from "react";
import { Link } from "react-router-dom";
import { AdminSiteContext } from "../contexts/AdminSiteContext";
//MUI Components
import { makeStyles } from "@material-ui/core/styles";
import { DataGrid, GridToolbar } from "@material-ui/data-grid";
import Typography from "@material-ui/core/Typography";
import { Button, Tooltip } from "@material-ui/core";
import { IconButton } from "@material-ui/core";
import DeleteIcon from "@material-ui/icons/Delete";
import AddIcon from "@material-ui/icons/Add";
import EditIcon from "@material-ui/icons/Edit";

const useStyles = makeStyles((theme) => ({
	datagrid: {
		backgroundColor: "white",
		//Remove outlines when clicking a cell in the datagrid
		"&.MuiDataGrid-root .MuiDataGrid-cell:focus-within": {
			outline: 0,
		},
		//Remove outlines when clicking a column header
		"&.MuiDataGrid-root .MuiDataGrid-colCell:focus": {
			outline: 0,
		},
	},
	slugLink: {
		color: theme.palette.primary.main,
	},
}));

export default function Units() {
	const classes = useStyles();
	const [rows, setRows] = useState([]);
	const [selectionModel, setSelectionModel] = useState([]);
	const [sortModel, setSortModel] = useState([{ field: "name", sort: "asc" }]);
	const { adminSelectedSite } = useContext(AdminSiteContext);

	useEffect(() => {
		const fetchRows = async () => {
			let res;
			if (adminSelectedSite && adminSelectedSite._id) {
				res = await fetch(
					`${process.env.REACT_APP_API_URL}/units?site=${adminSelectedSite._id}`
				);
			} else {
				res = await fetch(`${process.env.REACT_APP_API_URL}/units`);
			}
			const data = await res.json();
			return data;
		};

		const getRows = async () => {
			let rowsFromServer = await fetchRows();
			setRows(rowsFromServer.data);
		};
		getRows();
	}, [adminSelectedSite]);

	const deleteFromAPI = async (id) => {
		try {
			await fetch(`${process.env.REACT_APP_API_URL}/units/${id}`, {
				method: "DELETE",
				credentials: "include",
			});
		} catch (e) {
			console.log(e);
		}
	};

	const onClickDeleteAll = async () => {
		let confirmDelete = window.prompt(
			'Deleting a unit will delete all of its contents. Please enter "DELETE" below to confirm deleting the selected units.'
		);

		// Confirm before continuing
		if (confirmDelete && confirmDelete == "DELETE") {
			selectionModel.forEach((id) => {
				deleteFromAPI(id);
			});

			//Remove selectionModeled items from the rows state
			let newRows = [...rows];
			for (let i = 0; i < selectionModel.length; i++) {
				newRows = newRows.filter((row) => row._id !== selectionModel[i]);
			}
			await setRows(newRows);

			//Clear selection
			setSelectionModel([]);
		} else if (confirmDelete && confirmDelete !== "DELETE") {
			window.alert('"DELETE" must be typed in all caps. Please try again.');
		}
	};

	const columns = useMemo(
		() => [
			{ field: "_id", headerName: "ID", hide: true, width: 225 },
			{
				field: "name",
				headerName: "Name",
				renderCell: (params) => (
					<React.Fragment>
						<IconButton
							component={Link}
							to={`/${process.env.REACT_APP_ADMIN_URL}/units/${params.row.id}`}
							style={{ marginRight: ".5rem" }}
							aria-label="edit"
						>
							<EditIcon />
						</IconButton>
						<Tooltip title={params.value} placement="bottom-start">
							<p>{params.value}</p>
						</Tooltip>
					</React.Fragment>
				),
				width: 500,
			},
			{
				field: "published",
				headerName: "Status",
				valueGetter: (params) => {
					if (params.value) {
						return "Published";
					} else {
						return "Draft";
					}
				},
				renderCell: (params) => {
					if (params.value === "Published") {
						return (
							<Typography variant="body2" color="primary">
								<strong>{params.value}</strong>
							</Typography>
						);
					} else {
						return (
							<Typography variant="body2" color="secondary">
								<strong>{params.value}</strong>
							</Typography>
						);
					}
				},
				width: 120,
			},
			{
				field: "parent",
				headerName: "Parent",
				width: 250,
				valueGetter: (params) => {
					if (params.row.parent) {
						return params.row.parent.name;
					}
				},
			},
			{
				field: "site",
				headerName: "Site",
				width: 300,
				valueGetter: (params) => {
					if (params.row.site) {
						return params.row.site.name;
					}
				},
				renderCell: (params) => {
					return (
						<Tooltip title={params.row.site.name} placement="bottom-start">
							<Typography component="span" variant="body2">
								<Link
									className={classes.slugLink}
									to={`/${params.row.site.slug}`}
									target="_blank"
								>
									{params.row.site.name}
								</Link>
							</Typography>
						</Tooltip>
					);
				},
			},
			{
				field: "slug",
				headerName: "Slug",
				width: 225,
				renderCell: (params) => {
					return (
						<Tooltip title={`/${params.row.slug}`} placement="bottom-start">
							<Typography component="span" variant="body2">
								<Link
									className={classes.slugLink}
									to={`/${params.row.site.slug}/units/${params.row.slug}`}
									target="_blank"
								>
									{params.row.slug}
								</Link>
							</Typography>
						</Tooltip>
					);
				},
			},
			{
				field: "entries",
				headerName: "Entries",
				width: 130,
				renderCell: (params) => params.value.length || "0",
				type: "number",
			},
			{
				field: "people",
				headerName: "People",
				width: 130,
				renderCell: (params) => params.value.length || "0",
				type: "number",
			},
		],
		[classes.slugLink]
	);

	return (
		<>
			<div
				style={{ display: "flex", alignItems: "center", marginBottom: "1rem" }}
			>
				<Typography component="h1" variant="h4">
					Units{" "}
					{adminSelectedSite &&
						adminSelectedSite !== "" &&
						` in ${adminSelectedSite.name}`}
				</Typography>
				<Button
					startIcon={<AddIcon />}
					style={{ marginLeft: "1rem" }}
					color="primary"
					variant="contained"
					component={Link}
					to={`/${process.env.REACT_APP_ADMIN_URL}/units/add`}
				>
					Add New
				</Button>
				{selectionModel.length > 0 && (
					<Button
						startIcon={<DeleteIcon />}
						style={{ marginLeft: "1rem" }}
						color="secondary"
						variant="contained"
						onClick={async () => {
							//Remove from database
							await onClickDeleteAll();
						}}
					>
						Delete
					</Button>
				)}
			</div>
			<DataGrid
				className={classes.datagrid}
				components={{
					Toolbar: GridToolbar,
				}}
				getRowId={(row) => row._id}
				autoHeight
				rows={rows}
				columns={columns}
				checkboxSelection
				sortModel={sortModel}
				onSortModelChange={(model) => setSortModel(model)}
				selectionModel={selectionModel}
				onSelectionModelChange={(newSelection) => {
					setSelectionModel(newSelection);
				}}
			/>
		</>
	);
}
